/*
 *   File:           types.ts
 *   Description:    Collection of utility constants, helpers and misc
 *                   constants for the Admin Module
 *   Author:         PreVeil, LLC
 */
import { AccountType, CollectionServerUser, DataExportRequest, JSONOrgInfo, AdminUserBase } from "@preveil-api";
import { UserRequest, ApprovalGroup, UserValidationState, Account, RequestExportApiResponse, RequestTypesType } from "src/common";
import keyMirror from "keymirror";

export type UserInviteMap = { [key: string]: OrgInviteEntity };

export type OrgInviteEntity = {
  email: string;
  first_name: string;
  last_name: string;
  department: string;
  account_type?: AccountType;
}

export type UserValidationType = keyof typeof UserValidationState;

export type PendingAgStatusByUser = { [canonical_user_id: string]: { pending_recovery_group_id: string | null, recovery_group_pending: boolean } };

export type PendingGroupUpdatesByUser = { [user_id: string]: Array<{ request_id: string, request_type: RequestTypesType }> };

export type ResolvableOrgUser = {
  user_id: string;
  display_name: string;
};

export type OrganizationInformation = {
  org_info: JSONOrgInfo;
  approval_groups: ApprovalGroup[];
  org_users: CollectionServerUser[];
  total_request_rows: number;
  org_requests: UserRequest[];
  is_history_request: boolean;
  pending_group_updates: PendingGroupUpdatesByUser;
  lightweight_org_users: AdminUserBase[];
  pending_ag_status_by_user: PendingAgStatusByUser;
  export_request?: any; // TO DO: leaving this untyped for now as it will be used while working on the Data Export ticket (NEW-123)
  approver_shards?: string[];
};

export type ApprovalGroupRoles = "admin_approval_group" | "export_approval_group";

export type DataExportAllContent = {
  mail: boolean;
  drive: boolean;
  logs: boolean;
  acl_report: boolean;
  include_all_versions: boolean;
};

export type RequestMetadata = {
  include_emails: boolean;
  include_files: boolean;
  include_logs: boolean;
  include_org_acl_report: boolean;
  users: Account[];
} & RequestExportApiResponse;

export type ExportRequest = {
  request_id?: string;
  collection_id?: string;
  group_id?: string;
  group_version?: string;
  requester?: string;
  type?: string;
  status?: string;
  payload?: string;
  timestamp?: string;
  expiration?: string;
  started?: boolean;
  signature?: string;
  protocol_version?: number;
  __dirty__?: boolean;
  metadata?: DataExportRequest;
}

export type EmailGatewayType = "domain" | "individual";

export type GatewayConnectivityStatus = {
  healthy: boolean;
  details: string;
  version: boolean | null | string;
};

export type EmailGatewayEntity = {
  display_name: string;
  email_address: string;
  type: EmailGatewayType;
}

export type DeviceObject = {
  user_id: string;
  device_name: string;
  device_id: string;
  user_name: string;
  platform: string;
  active: string;
  locked_by?: string | null;
  lock_time?: string | null;
}

// Dashboard types
export type ChartDataInput = Record<string, string | number>;
export type ChartCellType = { key: string; fill: string }
export type ChartsData = { totals: ChartDataInput[], used: ChartDataInput[] };

export const AccountSelectionTypes = keyMirror({
  full: null,
  express: null,
  custom: null,
});
export type AccountSelectionType = keyof typeof AccountSelectionTypes;
